import { useEffect, useRef } from 'react';
import { reaction } from 'mobx';

import { tableStore } from '../../stores/table-store';
import { fetchVersionFileContent } from '../../utils/fetch-version-file-content';
import { uploadFile } from '../../utils/upload-file';
import type { ProStudioContext } from '../../xstate/prostudio.xstate.types';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { sendUserMessage } from '../../xstate/app.xstate-utils';
import {
  hasPendingKnowledgeSetup,
  takePendingKnowledgeSetup,
} from './knowledge-keys';
import { RAG_SEARCH_COMMAND, runKnowledgeSetup } from './run-knowledge-setup';

const TOAST_ID = 'bobcat-knowledge-setup';

const postKnowledge = async <T>(route: string, body: unknown): Promise<T> => {
  const res = await fetch(`/ai/knowledge/${route}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => null)) as
    | (T & { detail?: string; error?: string })
    | null;
  if (!res.ok || !json) {
    throw new Error(json?.detail ?? json?.error ?? res.statusText);
  }
  return json;
};

const searchNode = () =>
  tableStore.rows.find(
    (r) =>
      r.command === RAG_SEARCH_COMMAND ||
      // The AI-Native template's search (`ai-native-swap.ts`).
      r.command === `AsyncSys${RAG_SEARCH_COMMAND}`
  );

/**
 * Runs Talk it through's pending knowledge setup (`knowledge-keys.ts`) once
 * the solution it created is open: its draft version known and its rows,
 * search node included, loaded. Mounted once, at the app root.
 */
export const useKnowledgeSetup = () => {
  const { uniqueId, projectId, versionId } = useProStudioCtxSelector(
    (ctx: ProStudioContext) => ({
      uniqueId: ctx.selectedBot?.uniqueId,
      projectId: ctx.project?.id,
      versionId:
        ctx.customerId && ctx.botName && ctx.draftVersion
          ? `${ctx.customerId}.${ctx.botName}.${ctx.draftVersion}`
          : undefined,
    }),
    { isEqualityDeep: true }
  );
  const running = useRef(false);

  useEffect(() => {
    if (!uniqueId || !projectId || !versionId) return undefined;
    if (!hasPendingKnowledgeSetup(uniqueId) || running.current) {
      return undefined;
    }

    const start = () => {
      if (running.current || !tableStore.rows.length) return;
      const setup = takePendingKnowledgeSetup(uniqueId);
      if (!setup) return;
      running.current = true;

      const progress = (text: string) =>
        sendUserMessage({
          type: 'info',
          header: 'Setting up your agents',
          text,
          toastId: TOAST_ID,
          autoClose: false,
        });

      runKnowledgeSetup(setup, {
        progress,
        hasSearchNode: () => !!searchNode(),
        uploadScript: (file) =>
          new Promise<void>((resolve, reject) => {
            uploadFile({
              url: `${import.meta.env.FE_ADMIN_URL}/rest/bot-manager/${projectId}/${versionId}/scripts`,
              file: [file],
              type: 'scriptFile',
              onChange: ({ data }) => {
                if (data) {
                  sendProStudioEvent({
                    type: 'updateDraftData',
                    draft: data as ProStudioContext['draft'],
                    action: 'upload',
                  });
                }
                resolve();
              },
              error: (err) =>
                reject(new Error(`Couldn't add ${file.name}: ${String(err)}`)),
            });
          }),
        readScript: (path) =>
          fetchVersionFileContent({ projectId, versionId, filePath: path }),
        post: postKnowledge,
        setCorpusId: (corpusId) => {
          const node = searchNode();
          if (!node) return;
          const params =
            typeof node.parameterInput === 'string'
              ? JSON.parse(node.parameterInput || '{}')
              : { ...(node.parameterInput ?? {}) };
          tableStore.updateRow({
            rowId: node.id,
            updates: {
              parameterInput: {
                ...params,
                live_corpus_id: corpusId,
                sandbox_corpus_id: corpusId,
              },
            },
            saveImmediately: true,
          });
        },
      })
        .then((r) =>
          sendUserMessage({
            type: 'success',
            header: 'Your agents are set up',
            text: r.corpusId
              ? `Added ${r.keysAdded} credential file${r.keysAdded === 1 ? '' : 's'} and built the knowledge base from ${r.documents} document${r.documents === 1 ? '' : 's'} in ${r.projectId}.`
              : `Added ${r.keysAdded} credential file${r.keysAdded === 1 ? '' : 's'}.`,
            toastId: TOAST_ID,
            autoClose: 8000,
          })
        )
        .catch((err: unknown) =>
          sendUserMessage({
            type: 'error',
            header: "The knowledge base couldn't be set up",
            text: err instanceof Error ? err.message : String(err),
            toastId: TOAST_ID,
            autoClose: false,
          })
        )
        .finally(() => {
          running.current = false;
        });
    };

    start();
    // Rows land after the version: wait for them.
    return reaction(
      () => tableStore.rows.length,
      () => start()
    );
  }, [uniqueId, projectId, versionId]);
};
